import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Book, Trash2, Edit3, Plus, Search, Filter, AlertCircle, CheckCircle, Eye, Download, Users, Globe, ArrowLeft, FileText, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { adminService } from '../../services/adminService';
import { fixEncoding } from '../../utils/textCleaner';

// ─── Toast Component ─────────────────────────────────────────────────────────
const Toast = ({ message, type = 'success', onClose }) => {
  useEffect(() => {
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, [onClose]);

  return (
    <div className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-5 py-3.5 rounded-xl shadow-xl border text-sm font-medium animate-fade-in
      ${type === 'success' ? 'bg-success/15 border-success/30 text-success' : 'bg-destructive/15 border-destructive/30 text-destructive'}`}>
      {type === 'success' ? <CheckCircle className="w-4 h-4 shrink-0" /> : <AlertCircle className="w-4 h-4 shrink-0" />}
      {message}
      <button onClick={onClose} className="ml-2 opacity-60 hover:opacity-100">
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
};

// ─── Delete Confirmation Modal ────────────────────────────────────────────────
const DeleteConfirmModal = ({ version, onConfirm, onCancel, loading }) => {
  if (!version) return null;
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onCancel} />
      <div className="relative bg-card border border-border rounded-2xl shadow-2xl w-full max-w-md p-6 z-10">
        <div className="flex items-center justify-center w-14 h-14 rounded-full bg-destructive/15 mx-auto mb-4">
          <Trash2 className="w-7 h-7 text-destructive" />
        </div>
        <h2 className="text-lg font-bold text-foreground text-center mb-2">Delete Bible Version</h2>
        <p className="text-muted-foreground text-sm text-center mb-1">
          You are about to permanently delete:
        </p>
        <p className="text-foreground font-semibold text-center text-base mb-1">
          "{version?.versionName}"
          {version?.shortName ? ` (${version?.shortName})` : ''}
        </p>
        {version?.totalVerses > 0 && (
          <p className="text-destructive/80 text-xs text-center mb-4">
            This will also delete {version?.totalVerses?.toLocaleString()} associated verses.
          </p>
        )}
        <p className="text-muted-foreground text-xs text-center mb-6">
          This action <span className="font-semibold text-destructive">cannot be undone</span>.
        </p>
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            disabled={loading}
            className="flex-1 px-4 py-2.5 border border-border rounded-xl text-sm font-medium text-foreground hover:bg-muted/50 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className="flex-1 px-4 py-2.5 bg-destructive text-destructive-foreground rounded-xl text-sm font-semibold hover:bg-destructive/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? (
              <><div className="w-4 h-4 border-2 border-destructive-foreground/40 border-t-destructive-foreground rounded-full animate-spin" />Deleting...</>
            ) : (
              <><Trash2 className="w-4 h-4" />Confirm Delete</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Bulk Delete Confirmation Modal ──────────────────────────────────────────
const BulkDeleteConfirmModal = ({ count, onConfirm, onCancel, loading }) => (
  <div className="fixed inset-0 z-40 flex items-center justify-center p-4">
    <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onCancel} />
    <div className="relative bg-card border border-border rounded-2xl shadow-2xl w-full max-w-md p-6 z-10">
      <div className="flex items-center justify-center w-14 h-14 rounded-full bg-destructive/15 mx-auto mb-4">
        <Trash2 className="w-7 h-7 text-destructive" />
      </div>
      <h2 className="text-lg font-bold text-foreground text-center mb-2">Delete {count} Version{count > 1 ? 's' : ''}</h2>
      <p className="text-muted-foreground text-sm text-center mb-4">
        All selected Bible versions and their associated verses will be permanently removed.
      </p>
      <p className="text-muted-foreground text-xs text-center mb-6">
        This action <span className="font-semibold text-destructive">cannot be undone</span>.
      </p>
      <div className="flex gap-3">
        <button onClick={onCancel} disabled={loading} className="flex-1 px-4 py-2.5 border border-border rounded-xl text-sm font-medium text-foreground hover:bg-muted/50 transition-colors disabled:opacity-50">
          Cancel
        </button>
        <button onClick={onConfirm} disabled={loading} className="flex-1 px-4 py-2.5 bg-destructive text-destructive-foreground rounded-xl text-sm font-semibold hover:bg-destructive/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
          {loading ? <><div className="w-4 h-4 border-2 border-destructive-foreground/40 border-t-destructive-foreground rounded-full animate-spin" />Deleting...</> : <><Trash2 className="w-4 h-4" />Confirm Delete</>}
        </button>
      </div>
    </div>
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const BibleVersionManagement = () => {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [languageFilter, setLanguageFilter] = useState('');
  const [selectedVersions, setSelectedVersions] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);

  // Modal state
  const [deleteTarget, setDeleteTarget] = useState(null); // single version object
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Toast state
  const [toast, setToast] = useState(null); // { message, type }

  const navigate = useNavigate();
  const { userProfile, isDemoAdmin } = useAuth();

  const showToast = (message, type = 'success') => setToast({ message, type });
  const hideToast = () => setToast(null);

  useEffect(() => {
    checkAdminAccess();
  }, [userProfile, isDemoAdmin]);

  useEffect(() => {
    if (isAdmin) {
      loadVersions();
    }
  }, [isAdmin]);

  const checkAdminAccess = async () => {
    try {
      setLoading(true);
      const contextIsAdmin = userProfile?.role === 'admin';
      const contextIsDemoAdmin = isDemoAdmin === true;
      if (contextIsAdmin || contextIsDemoAdmin) {
        setIsAdmin(true);
        return;
      }
      try {
        const adminStatus = await adminService?.isAdmin();
        if (adminStatus) {
          setIsAdmin(true);
          return;
        }
      } catch (serviceError) {
        console.warn('AdminService check failed:', serviceError);
      }
      setError('Access denied. Administrator privileges required.');
      setTimeout(() => navigate('/dashboard'), 2000);
    } catch (err) {
      console.error('Admin access check failed:', err);
      setError('Access verification failed. Redirecting to dashboard.');
      setTimeout(() => navigate('/dashboard'), 2000);
    } finally {
      setLoading(false);
    }
  };

  const loadVersions = async () => {
    try {
      setLoading(true);
      const versionsData = await adminService?.getBibleVersionsWithStats();
      setVersions(versionsData || []);
      setError('');
    } catch (err) {
      console.error('Error loading Bible versions:', err);
      setError('Failed to load Bible versions: ' + err?.message);
    } finally {
      setLoading(false);
    }
  };

  // ── Single delete ──
  const handleDeleteVersion = (version) => {
    setDeleteTarget(version);
  };

  const confirmDeleteVersion = async () => {
    if (!deleteTarget) return;
    try {
      setDeleteLoading(true);
      setError('');
      const result = await adminService?.deleteBibleVersion(deleteTarget?.id);
      if (result?.success) {
        await loadVersions();
        setSelectedVersions(prev => prev?.filter(id => id !== deleteTarget?.id));
        showToast(`"${deleteTarget?.versionName}" deleted successfully.`);
      } else {
        setError(result?.error || 'Failed to delete Bible version');
        showToast(result?.error || 'Failed to delete Bible version', 'error');
      }
    } catch (err) {
      console.error('Delete error:', err);
      const msg = 'Failed to delete Bible version: ' + (err?.message || 'Unknown error');
      setError(msg);
      showToast(msg, 'error');
    } finally {
      setDeleteLoading(false);
      setDeleteTarget(null);
    }
  };

  // ── Bulk delete ──
  const handleBulkDelete = () => {
    if (selectedVersions?.length === 0) return;
    setShowBulkDeleteModal(true);
  };

  const confirmBulkDelete = async () => {
    try {
      setDeleteLoading(true);
      setError('');
      const result = await adminService?.bulkDeleteBibleVersions(selectedVersions);
      if (result?.success) {
        const count = selectedVersions?.length;
        await loadVersions();
        setSelectedVersions([]);
        showToast(`${count} Bible version${count > 1 ? 's' : ''} deleted successfully.`);
      } else {
        setError(result?.error || 'Failed to delete selected Bible versions');
        showToast(result?.error || 'Failed to delete selected versions', 'error');
      }
    } catch (err) {
      console.error('Bulk delete error:', err);
      const msg = 'Failed to delete selected Bible versions: ' + (err?.message || 'Unknown error');
      setError(msg);
      showToast(msg, 'error');
    } finally {
      setDeleteLoading(false);
      setShowBulkDeleteModal(false);
    }
  };

  const handleToggleVersion = async (versionId, currentStatus) => {
    try {
      setError('');
      const result = await adminService?.toggleBibleVersionStatus(versionId, !currentStatus);
      if (result?.success) {
        await loadVersions();
      } else {
        setError(result?.error || 'Failed to update Bible version status');
      }
    } catch (err) {
      console.error('Toggle error:', err);
      setError('Failed to update Bible version status: ' + (err?.message || 'Unknown error'));
    }
  };

  const handleViewVersion = async (versionId) => {
    try {
      setError('');
      await adminService?.getBibleVersionDetails(versionId);
    } catch (err) {
      setError('Failed to fetch version details: ' + err?.message);
    }
  };

  const handleEditVersion = (versionId) => {
    console.log('Edit version:', versionId);
  };

  const handleExportVersion = async (version) => {
    try {
      setError('');
      await adminService?.downloadBibleVersionExport(version?.id, version?.versionName);
    } catch (err) {
      setError('Failed to export Bible version: ' + err?.message);
    }
  };

  const filteredVersions = versions?.filter(version => {
    const matchesSearch = !searchTerm ||
      version?.versionName?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      version?.shortName?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      version?.description?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    const matchesLanguage = !languageFilter || version?.language === languageFilter;
    return matchesSearch && matchesLanguage;
  });

  const uniqueLanguages = [...new Set(versions?.map(v => v?.language)?.filter(Boolean))];

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const formatNumber = (num) => {
    if (num === 0) return '0';
    return num?.toLocaleString() || '0';
  };

  const handleVersionSelect = (versionId) => {
    setSelectedVersions(prev =>
      prev?.includes(versionId) ? prev?.filter(id => id !== versionId) : [...prev, versionId]
    );
  };

  const handleSelectAll = () => {
    if (selectedVersions?.length === filteredVersions?.length) {
      setSelectedVersions([]);
    } else {
      setSelectedVersions(filteredVersions?.map(v => v?.id));
    }
  };

  const totalVersesAcrossVersions = versions?.reduce((sum, version) => sum + (version?.totalVerses || 0), 0);

  if (!isAdmin && error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <AlertCircle className="w-16 h-16 text-destructive mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-foreground mb-2">{error}</h2>
          <p className="text-muted-foreground mb-4">You will be redirected shortly.</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="text-sm text-primary hover:text-primary/80 underline"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Modals */}
      {deleteTarget && (
        <DeleteConfirmModal
          version={deleteTarget}
          onConfirm={confirmDeleteVersion}
          onCancel={() => setDeleteTarget(null)}
          loading={deleteLoading}
        />
      )}
      {showBulkDeleteModal && (
        <BulkDeleteConfirmModal
          count={selectedVersions?.length}
          onConfirm={confirmBulkDelete}
          onCancel={() => setShowBulkDeleteModal(false)}
          loading={deleteLoading}
        />
      )}
      {/* Toast */}
      {toast && <Toast message={toast?.message} type={toast?.type} onClose={hideToast} />}
      {/* Header */}
      <div className="bg-card shadow-sm border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-6">
            <div className="flex items-center space-x-4">
              <Link
                to="/admin-dashboard"
                className="flex items-center text-muted-foreground hover:text-foreground transition-colors"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back to Admin Dashboard
              </Link>
              <div className="h-6 border-l border-border"></div>
              <div>
                <h1 className="text-2xl font-bold text-foreground flex items-center">
                  <Book className="w-8 h-8 mr-3 text-primary" />
                  Bible Version Management
                </h1>
                <p className="text-muted-foreground">Manage Bible versions and translations</p>
              </div>
            </div>
            <Link
              to="/bible-version-import"
              className="bg-primary hover:bg-primary/90 text-primary-foreground px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Import New Version
            </Link>
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <AlertCircle className="w-5 h-5 text-destructive mr-2" />
              <span className="text-destructive">{error}</span>
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="text-muted-foreground mt-4">Loading Bible versions...</p>
          </div>
        ) : (
          <>
            {/* Search and Filters */}
            <div className="bg-card rounded-lg shadow-sm p-6 mb-6 border border-border">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="w-5 h-5 text-muted-foreground absolute left-3 top-1/2 transform -translate-y-1/2" />
                    <input
                      type="text"
                      placeholder="Search versions by name, short name, or description..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e?.target?.value)}
                      className="pl-10 pr-4 py-2 w-full border border-border rounded-lg bg-input text-foreground focus:ring-2 focus:ring-ring focus:border-transparent"
                    />
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="relative">
                    <Filter className="w-5 h-5 text-muted-foreground absolute left-3 top-1/2 transform -translate-y-1/2" />
                    <select
                      value={languageFilter}
                      onChange={(e) => setLanguageFilter(e?.target?.value)}
                      className="pl-10 pr-8 py-2 border border-border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent appearance-none bg-input text-foreground"
                    >
                      <option value="">All Languages</option>
                      {uniqueLanguages?.map(lang => (
                        <option key={lang} value={lang}>{lang}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {selectedVersions?.length > 0 && (
                <div className="mt-4 flex items-center justify-between bg-primary/10 border border-primary/30 rounded-lg p-3">
                  <span className="text-primary">
                    {selectedVersions?.length} version(s) selected
                  </span>
                  <button
                    onClick={handleBulkDelete}
                    className="text-destructive hover:text-destructive/80 flex items-center gap-1"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete Selected
                  </button>
                </div>
              )}
            </div>

            {/* Statistics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
              <div className="bg-card rounded-lg shadow-sm p-6 border border-border">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Total Versions</p>
                    <p className="text-2xl font-bold text-foreground">{versions?.length || 0}</p>
                  </div>
                  <div className="bg-primary/10 p-3 rounded-full">
                    <Book className="w-6 h-6 text-primary" />
                  </div>
                </div>
              </div>

              <div className="bg-card rounded-lg shadow-sm p-6 border border-border">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Total Verses</p>
                    <p className="text-2xl font-bold text-foreground">{formatNumber(totalVersesAcrossVersions)}</p>
                  </div>
                  <div className="bg-success/10 p-3 rounded-full">
                    <FileText className="w-6 h-6 text-success" />
                  </div>
                </div>
              </div>

              <div className="bg-card rounded-lg shadow-sm p-6 border border-border">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Languages</p>
                    <p className="text-2xl font-bold text-foreground">{uniqueLanguages?.length || 0}</p>
                  </div>
                  <div className="bg-accent/10 p-3 rounded-full">
                    <Globe className="w-6 h-6 text-accent" />
                  </div>
                </div>
              </div>

              <div className="bg-card rounded-lg shadow-sm p-6 border border-border">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Active Versions</p>
                    <p className="text-2xl font-bold text-foreground">{versions?.filter(v => v?.isEnabled !== false)?.length || 0}</p>
                  </div>
                  <div className="bg-success/10 p-3 rounded-full">
                    <CheckCircle className="w-6 h-6 text-success" />
                  </div>
                </div>
              </div>
            </div>

            {/* Bible Versions Table */}
            <div className="bg-card rounded-lg shadow-sm overflow-hidden border border-border">
              <div className="px-6 py-4 border-b border-border">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-foreground">
                    Bible Versions ({filteredVersions?.length})
                  </h2>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={selectedVersions?.length === filteredVersions?.length && filteredVersions?.length > 0}
                      onChange={handleSelectAll}
                      className="rounded border-border text-primary focus:ring-ring"
                    />
                    <span className="text-sm text-muted-foreground">Select All</span>
                  </div>
                </div>
              </div>

              {filteredVersions?.length === 0 ? (
                <div className="text-center py-12">
                  <Book className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground text-lg">No Bible versions found</p>
                  <p className="text-muted-foreground/70">
                    {searchTerm || languageFilter
                      ? 'Try adjusting your search or filter criteria' : 'Start by importing your first Bible version'}
                  </p>
                  <Link
                    to="/bible-version-import"
                    className="mt-4 inline-flex items-center px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Import Bible Version
                  </Link>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-border">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          <input
                            type="checkbox"
                            checked={selectedVersions?.length === filteredVersions?.length && filteredVersions?.length > 0}
                            onChange={handleSelectAll}
                            className="rounded border-border text-primary focus:ring-ring"
                          />
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Version</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Verses</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Language</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Created</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Creator</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-card divide-y divide-border">
                      {filteredVersions?.map((version) => (
                        <tr key={version?.id} className="hover:bg-muted/30 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <input
                              type="checkbox"
                              checked={selectedVersions?.includes(version?.id)}
                              onChange={() => handleVersionSelect(version?.id)}
                              className="rounded border-border text-primary focus:ring-ring"
                            />
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-10 w-10">
                                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                                  <Book className="w-5 h-5 text-primary" />
                                </div>
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-foreground">{fixEncoding(version?.versionName)}</div>
                                <div className="text-sm text-muted-foreground">{version?.shortName}</div>
                                {version?.description && (
                                  <div className="text-xs text-muted-foreground/70 max-w-xs truncate">{fixEncoding(version?.description)}</div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-foreground">
                              <div className="flex items-center">
                                <FileText className="w-4 h-4 text-muted-foreground mr-2" />
                                <span className="font-medium">{formatNumber(version?.totalVerses)}</span>
                                <span className="text-muted-foreground ml-1">verses</span>
                              </div>
                              {version?.totalBooks > 0 && (
                                <div className="text-xs text-muted-foreground mt-1">
                                  {version?.totalBooks} books, {formatNumber(version?.totalChapters)} chapters
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-accent/15 text-accent">
                              <Globe className="w-3 h-3 mr-1" />
                              {version?.language}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                            {formatDate(version?.createdAt)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <Users className="w-4 h-4 text-muted-foreground mr-2" />
                              <span className="text-sm text-foreground">
                                {version?.creator?.full_name || version?.creator?.email || 'Unknown'}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              version?.isEnabled !== false
                                ? 'bg-success/15 text-success' : 'bg-muted text-muted-foreground'
                            }`}>
                              {version?.isEnabled !== false ? (
                                <><CheckCircle className="w-3 h-3 mr-1" />Active</>
                              ) : (
                                <><AlertCircle className="w-3 h-3 mr-1" />Inactive</>
                              )}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex items-center justify-end space-x-2">
                              <button
                                onClick={() => handleViewVersion(version?.id)}
                                className="text-primary hover:text-primary/80 p-2 rounded-full hover:bg-primary/10 transition-colors"
                                title="View Details"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleEditVersion(version?.id)}
                                className="text-warning hover:text-warning/80 p-2 rounded-full hover:bg-warning/10 transition-colors"
                                title="Edit Version"
                              >
                                <Edit3 className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleExportVersion(version)}
                                className="text-success hover:text-success/80 p-2 rounded-full hover:bg-success/10 transition-colors"
                                title="Export Version"
                              >
                                <Download className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleToggleVersion(version?.id, version?.isEnabled)}
                                className={`p-2 rounded-full transition-colors ${
                                  version?.isEnabled !== false
                                    ? 'text-warning hover:text-warning/80 hover:bg-warning/10' : 'text-success hover:text-success/80 hover:bg-success/10'
                                }`}
                                title={version?.isEnabled !== false ? 'Disable Version' : 'Enable Version'}
                              >
                                {version?.isEnabled !== false ? (
                                  <AlertCircle className="w-4 h-4" />
                                ) : (
                                  <CheckCircle className="w-4 h-4" />
                                )}
                              </button>
                              <button
                                onClick={() => handleDeleteVersion(version)}
                                className="text-destructive hover:text-destructive/80 p-2 rounded-full hover:bg-destructive/10 transition-colors"
                                title="Delete Version"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default BibleVersionManagement;